/**
 * Minimal JWT helpers for client-side expiry checks.
 *
 * This is a UX optimisation only, never a security boundary. The token is not
 * verified here; the API always re-validates it. The point is to notice a token
 * has expired *before* firing a request that is guaranteed to 401, so the user
 * gets "please log in again" instead of "failed to create product".
 */

interface JwtPayload {
  exp?: number; // seconds since epoch
  [key: string]: unknown;
}

/** Decode a JWT payload without verifying the signature. Returns null if malformed. */
export function decodeToken(token: string): JwtPayload | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    // base64url -> base64, then pad to a multiple of 4.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    // Handle UTF-8 payloads correctly (atob alone mangles multi-byte chars).
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );

    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

/** Expiry as epoch milliseconds, or null when the token has no usable `exp`. */
export function getTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  const payload = decodeToken(token);
  if (!payload?.exp) return null;
  return payload.exp * 1000;
}

/**
 * True when the token is missing, malformed, or past its expiry.
 *
 * `skewMs` treats a token that is about to expire as already expired, so we
 * don't start a request that will land after the deadline. Defaults to 10s.
 */
export function isTokenExpired(token: string | null, skewMs = 10_000): boolean {
  if (!token) return true;
  const expiresAt = getTokenExpiry(token);
  // A token we can't read an expiry from is left alone; the API decides.
  if (expiresAt === null) return false;
  return Date.now() + skewMs >= expiresAt;
}

/** Milliseconds until the token expires. 0 if already expired, null if unknown. */
export function getMsUntilExpiry(token: string | null): number | null {
  const expiresAt = getTokenExpiry(token);
  if (expiresAt === null) return null;
  return Math.max(0, expiresAt - Date.now());
}
